import { useState } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Space, Typography, App as AntApp } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useCreateInvitationMutation, type InvitationResponse, type UserRole } from './hooks';
import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';

const { Text, Paragraph } = Typography;

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Resend-режим: email и роль уже известны (из строки pending-приглашения).
   * Форму ввода не показываем — сразу подтверждение перевыпуска ссылки.
   */
  preset?: { email: string; role: UserRole };
}

interface InviteValues {
  email: string;
  role: UserRole;
}

export function InviteUserModal({ open, onClose, preset }: InviteUserModalProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<InviteValues>();
  const [result, setResult] = useState<InvitationResponse | null>(null);

  const isResend = preset != null;

  const mutation = useCreateInvitationMutation();

  const handleClose = (): void => {
    form.resetFields();
    setResult(null);
    mutation.reset();
    onClose();
  };

  const submit = (email: string, role: UserRole): void => {
    mutation.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (resp) => {
          setResult(resp);
        },
        onError: (err) => {
          // Не показываем глобальный toast для 409 — Alert внутри модалки нагляднее.
          if (!(err instanceof ApiError && err.status === 409)) {
            void message.error(err instanceof Error ? err.message : t('errors.generic'));
          }
        },
      },
    );
  };

  const onSubmit = (values: InviteValues): void => {
    submit(values.email, values.role);
  };

  // Backend в M2.1 формирует `invite_link` с placeholder-доменом
  // (`https://app.starling-light.example/accept-invite?token=...`) — для прод-
  // отправки нужен реальный домен оптики. До тех пор, пока backend не получит
  // `APP_BASE_URL` из env (backlog), пересобираем ссылку на фронте:
  // вытаскиваем token из invite_link и подставляем текущий origin.
  // Безопасно, потому что валидируется только сам token, а не домен.
  const localInviteLink = (() => {
    if (!result) return '';
    try {
      const token = new URL(result.invite_link).searchParams.get('token');
      if (!token) return result.invite_link;
      return `${window.location.origin}/accept-invite?token=${token}`;
    } catch {
      // На всякий случай — если invite_link невалидный URL, отдаём как есть.
      return result.invite_link;
    }
  })();

  const handleCopy = async (): Promise<void> => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(localInviteLink);
      void message.success(t('common.copied'));
    } catch {
      void message.error(t('errors.generic'));
    }
  };

  return (
    <Modal
      title={t(isResend ? 'users.invite_modal.resend_title' : 'users.invite_modal.title')}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        result ? (
          <Button type="primary" onClick={handleClose}>
            {t('common.close')}
          </Button>
        ) : isResend ? (
          <Space>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button
              type="primary"
              loading={mutation.isPending}
              onClick={() => submit(preset.email, preset.role)}
            >
              {t('users.invite_modal.resend_submit')}
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
              {t('users.invite_modal.submit')}
            </Button>
          </Space>
        )
      }
    >
      {!result && isResend && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            {t('users.invite_modal.resend_confirm', { email: preset.email })}
          </Paragraph>
          {mutation.error ? <ErrorAlert error={mutation.error} /> : null}
        </Space>
      )}

      {!result && !isResend && (
        <>
          <Paragraph type="secondary">{t('users.invite_modal.subtitle')}</Paragraph>
          <Form<InviteValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onSubmit}
            initialValues={{ role: 'employee' }}
          >
            <Form.Item
              label={t('users.invite_modal.email')}
              name="email"
              rules={[
                { required: true, message: t('users.invite_modal.email_required') },
                { type: 'email', message: t('users.invite_modal.email_invalid') },
              ]}
            >
              <Input placeholder="user@example.com" autoFocus />
            </Form.Item>
            <Form.Item
              label={t('users.invite_modal.role')}
              name="role"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: 'admin', label: t('users.role.admin') },
                  { value: 'employee', label: t('users.role.employee') },
                ]}
              />
            </Form.Item>
          </Form>
          {mutation.error instanceof ApiError && mutation.error.status === 409 ? (
            <Alert
              type="warning"
              showIcon
              message={t('users.invite_modal.errors.email_already_member')}
              style={{ marginTop: 8 }}
            />
          ) : mutation.error ? (
            <ErrorAlert error={mutation.error} />
          ) : null}
        </>
      )}

      {result && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message={t('users.invite_modal.success_title')}
            description={t('users.invite_modal.success_description', {
              email: result.invitation.email,
            })}
          />
          <div>
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {t('users.invite_modal.link_label')}
            </Text>
            <div className="sl-deep-link" data-testid="invite-link">
              {localInviteLink}
            </div>
          </div>
          <Button icon={<CopyOutlined />} onClick={handleCopy}>
            {t('users.invite_modal.copy')}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('users.invite_modal.expires_at', {
              date: new Date(result.invitation.expires_at).toLocaleDateString('ru-RU'),
            })}
          </Text>
        </Space>
      )}
    </Modal>
  );
}
